import { Plus, Sparkles } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { hapticTap } from '@/features/lifecycle/haptics';

export interface SmartBarProps {
  /** Opens the capture composer (the default job of the bar). */
  onOpenCapture: () => void;
}

/**
 * Docked mobile entry bar — one bar, both jobs. Tapping the bar opens the
 * capture composer (local, free, instant); the trailing ✦ chip opens Ask
 * Brainday. Replaces the AskBar + MobileFab pair — see
 * docs/specs/2026-07-03-smart-bar-capture-ask.md.
 *
 * Two sibling buttons in a container (never a button inside a button).
 * Visible on every screen (100+/day) — no entrance animation, per budget.
 */
export function SmartBar({ onOpenCapture }: SmartBarProps) {
  const setPaletteOpen = useUIStore((s) => s.setCommandPaletteOpen);
  // No --keyboard-height in the bottom offset: the WebView shrinks above the
  // native keyboard (resize: 'native'), so adding it double-compensates and
  // strands the bar mid-screen — the BottomTabs routine-builder bug.
  return (
    <div className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+64px)] z-bar flex items-center rounded-full border border-line-2 bg-card py-1 pe-1 ps-4 shadow-window">
      <button
        type="button"
        onClick={() => {
          void hapticTap();
          onOpenCapture();
        }}
        className="pressable -ms-4 flex min-w-0 flex-1 items-center gap-2 rounded-full py-2 ps-4 text-body text-t2 outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset pointer-coarse:min-h-[44px]"
      >
        <Plus className="h-4 w-4 shrink-0 text-t3" aria-hidden />
        <span className="truncate">Add a task — or ask Brainday…</span>
      </button>
      <button
        type="button"
        aria-label="Ask Brainday"
        onClick={() => {
          void hapticTap();
          setPaletteOpen(true);
        }}
        className="pressable relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand-ink outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-card pointer-coarse:before:absolute pointer-coarse:before:-inset-1.5 pointer-coarse:before:content-['']"
      >
        <Sparkles className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
